import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const SecuritySection = () => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handlePasswordChange = (e) => {
    const { name, value } = e?.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    if (passwordErrors?.[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const passwordRequirements = [
    { label: 'At least 8 characters', test: (pw) => pw?.length >= 8 },
    { label: 'Uppercase and lowercase letters', test: (pw) => /[a-z]/.test(pw) && /[A-Z]/.test(pw) },
    { label: 'At least one number', test: (pw) => /\d/.test(pw) },
  ];

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordForm?.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    if (!passwordForm?.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm?.newPassword?.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordForm?.newPassword)) {
      errors.newPassword = 'Password must contain uppercase, lowercase, and number';
    }
    if (!passwordForm?.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setPasswordErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePasswordForm()) return;

    setIsChangingPassword(true);
    setPasswordSuccess(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email,
        password: passwordForm.currentPassword,
      });

      if (signInError) {
        setPasswordErrors({ currentPassword: 'Current password is incorrect' });
        setIsChangingPassword(false);
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordForm.newPassword,
      });

      if (updateError) {
        setPasswordErrors({ newPassword: updateError.message || 'Failed to update password' });
        setIsChangingPassword(false);
        return;
      }

      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSuccess(true);
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordErrors({ currentPassword: error?.message || 'An unexpected error occurred' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="glassmorphic-card rounded-2xl p-6 md:p-8">
      <div className="mb-8">
        <h2 className="text-xl font-bold text-white mb-1">Security Settings</h2>
        <p className="text-sm text-white/40">Manage your password, two-factor authentication, and account security</p>
      </div>

      {passwordSuccess && (
        <div className="mb-6 p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
          <Icon name="CheckCircle" size={18} className="text-green-400" />
          <p className="text-sm text-green-400 font-medium">Password changed successfully!</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Change Password */}
        <div className="glassmorphic-card rounded-2xl p-5 md:p-6" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="KeyRound" size={20} className="text-orange-400" />
            <h3 className="text-base font-semibold text-white">Change Password</h3>
          </div>
          <p className="text-xs text-white/40 mb-6">Update your password to keep your account secure</p>

          <div className="space-y-4">
            {/* Current Password */}
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">Current Password</label>
              <div className="relative">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  className="w-full rounded-xl px-4 py-3 pr-10 text-sm text-white placeholder:text-white/30 focus:outline-none transition-all"
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: passwordErrors?.currentPassword ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                  }}
                  onFocus={(e) => { if (!passwordErrors?.currentPassword) e.target.style.border = '1px solid rgba(254, 131, 56, 0.5)'; }}
                  onBlur={(e) => { if (!passwordErrors?.currentPassword) e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)'; }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                >
                  <Icon name={showPasswords.current ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>
              {passwordErrors?.currentPassword && <p className="text-xs text-red-400 mt-1">{passwordErrors.currentPassword}</p>}
            </div>

            {/* New Password */}
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">New Password</label>
              <div className="relative">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  className="w-full rounded-xl px-4 py-3 pr-10 text-sm text-white placeholder:text-white/30 focus:outline-none transition-all"
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: passwordErrors?.newPassword ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                  }}
                  onFocus={(e) => { if (!passwordErrors?.newPassword) e.target.style.border = '1px solid rgba(254, 131, 56, 0.5)'; }}
                  onBlur={(e) => { if (!passwordErrors?.newPassword) e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)'; }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                >
                  <Icon name={showPasswords.new ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>
              {passwordErrors?.newPassword && <p className="text-xs text-red-400 mt-1">{passwordErrors.newPassword}</p>}
            </div>

            {/* Confirm New Password */}
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  className="w-full rounded-xl px-4 py-3 pr-10 text-sm text-white placeholder:text-white/30 focus:outline-none transition-all"
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: passwordErrors?.confirmPassword ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                  }}
                  onFocus={(e) => { if (!passwordErrors?.confirmPassword) e.target.style.border = '1px solid rgba(254, 131, 56, 0.5)'; }}
                  onBlur={(e) => { if (!passwordErrors?.confirmPassword) e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)'; }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                >
                  <Icon name={showPasswords.confirm ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>
              {passwordErrors?.confirmPassword && <p className="text-xs text-red-400 mt-1">{passwordErrors.confirmPassword}</p>}
            </div>

            {/* Update Password Button */}
            <button
              onClick={handlePasswordSubmit}
              disabled={isChangingPassword}
              className="w-full glassmorphic-button rounded-xl py-3 text-white text-sm font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
            >
              {isChangingPassword ? (
                <>
                  <Icon name="Loader" size={16} className="animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </button>
          </div>
        </div>

        {/* Password Requirements */}
        <div className="glassmorphic-card rounded-2xl p-5 md:p-6 h-fit" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
          <h3 className="text-base font-semibold text-white mb-1">Password Requirements</h3>
          <p className="text-xs text-white/40 mb-5">Your password must meet these criteria</p>

          <ul className="space-y-3.5">
            {passwordRequirements.map((req, idx) => {
              const isMet = passwordForm.newPassword && req.test(passwordForm.newPassword);
              return (
                <li key={idx} className="flex items-center gap-3">
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{
                      background: isMet ? 'rgba(34, 197, 94, 0.8)' : 'rgba(254, 131, 56, 0.6)',
                      boxShadow: isMet ? '0 0 6px rgba(34, 197, 94, 0.4)' : '0 0 6px rgba(254, 131, 56, 0.3)',
                    }}
                  />
                  <span className={`text-sm ${isMet ? 'text-green-400' : 'text-white/60'}`}>
                    {req.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SecuritySection;
